// ============================================================
// Tie-In — TriageApp.jsx
// Root component for the Email Triage module.
// Handles: auth state, onboarding gate, screen routing,
// nav bar, notification badge, top bar.
// ============================================================

import { useState, useEffect } from 'react';
import { t } from './triageTheme.js';
import { getConfig } from './triageApi.js';

import OnboardingFlow from './OnboardingFlow';
import TriageHistory  from './TriageHistory';
import TriageInbox    from './TriageInbox';
import TriageSettings from './TriageSettings';
import AdminTriage    from './AdminTriage';

// ── Nav icons ─────────────────────────────────────────────────
const NavIcons = {
  history: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
    </svg>
  ),
  inbox: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  settings: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  ),
  admin: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
    </svg>
  ),
};

// ── Screen title map ──────────────────────────────────────────
const SCREEN_TITLES = {
  history:  'Run history',
  inbox:    'Sorted inbox',
  settings: 'My settings',
  admin:    'Admin panel',
};

// ── Top bar ───────────────────────────────────────────────────
function TopBar({ screen, hasNotif, onNotifPress, config }) {
  const title = screen === 'history' ? 'Tie-In Triage' : SCREEN_TITLES[screen];
  const sub   = config?.gmail_email || 'Mint Roofing';

  return (
    <div style={{
      display:        'flex',
      alignItems:     'center',
      justifyContent: 'space-between',
      padding:        '14px 16px 12px',
      borderBottom:   `1px solid ${t.colors.border}`,
      background:     t.colors.bgElevated,
      flexShrink:     0,
    }}>
      <div>
        <div style={{
          fontSize:   '18px',
          fontWeight: 600,
          fontFamily: t.fonts.display,
          color:      t.colors.textPrimary,
          letterSpacing: '0.03em',
        }}>
          {title}
        </div>
        <div style={{
          fontSize:   '11px',
          color:      t.colors.textMuted,
          fontFamily: t.fonts.mono,
          marginTop:  '1px',
        }}>
          {sub}
        </div>
      </div>

      {/* Notification bell */}
      <button
        onClick={onNotifPress}
        style={{
          background:   'none',
          border:       'none',
          cursor:       'pointer',
          padding:      '6px',
          position:     'relative',
          color:        t.colors.textSecondary,
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        {hasNotif && (
          <div style={{
            position:     'absolute',
            top:          '4px',
            right:        '4px',
            width:        '8px',
            height:       '8px',
            borderRadius: '50%',
            background:   t.colors.danger,
            border:       `2px solid ${t.colors.bgElevated}`,
          }} />
        )}
      </button>
    </div>
  );
}

// ── Bottom nav ────────────────────────────────────────────────
function BottomNav({ screen, onChange, isAdmin, hasNotif }) {
  const tabs = [
    { key: 'history',  label: 'History' },
    { key: 'inbox',    label: 'Inbox'   },
    { key: 'settings', label: 'Settings'},
    ...(isAdmin ? [{ key: 'admin', label: 'Admin' }] : []),
  ];

  return (
    <div style={{
      display:       'flex',
      borderTop:     `1px solid ${t.colors.border}`,
      background:    t.colors.bgElevated,
      flexShrink:    0,
      paddingBottom: 'env(safe-area-inset-bottom, 0px)', // iPhone notch
    }}>
      {tabs.map(tab => {
        const active = screen === tab.key;
        return (
          <button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            style={{
              flex:           1,
              display:        'flex',
              flexDirection:  'column',
              alignItems:     'center',
              gap:            '3px',
              padding:        '10px 4px 8px',
              background:     'none',
              border:         'none',
              cursor:         'pointer',
              color:          active ? t.colors.accent : t.colors.textMuted,
              transition:     t.transitions.fast,
              position:       'relative',
            }}
          >
            {NavIcons[tab.key]}
            <span style={{ fontSize: '10px', fontFamily: t.fonts.body, fontWeight: active ? 500 : 400 }}>
              {tab.label}
            </span>
            {tab.key === 'history' && hasNotif && (
              <div style={{
                position:     'absolute',
                top:          '8px',
                right:        'calc(50% - 14px)',
                width:        '6px',
                height:       '6px',
                borderRadius: '50%',
                background:   t.colors.danger,
              }} />
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Loading screen ────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{
      flex:           1,
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      gap:            '12px',
    }}>
      <div style={{
        width:        '40px',
        height:       '40px',
        border:       `3px solid ${t.colors.border}`,
        borderTop:    `3px solid ${t.colors.accent}`,
        borderRadius: '50%',
        animation:    'spin 0.8s linear infinite',
      }} />
      <span style={{ fontSize: '13px', color: t.colors.textMuted, fontFamily: t.fonts.body }}>
        Loading…
      </span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export default function TriageApp({ userId, userRole }) {
  const [screen,      setScreen]      = useState('history');
  const [config,      setConfig]      = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [notification,setNotification]= useState(null);
  const [hasNotif,    setHasNotif]    = useState(false);

  const isAdmin = ['owner', 'leadership'].includes(userRole);

  useEffect(() => {
    if (!userId) return;
    getConfig(userId)
      .then(data => {
        setConfig(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [userId]);

  // Poll for notifications from Supabase in production.
  // For now, demo: check URL params for post-oauth redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected')) {
      // Re-fetch config after OAuth connect
      getConfig(userId).then(setConfig).catch(() => {});
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  function handleNotifPress() {
    setScreen('history');
    setHasNotif(false);
  }

  function handleOnboardingComplete() {
    getConfig(userId).then(data => {
      setConfig(data);
      setScreen('history');
    });
  }

  // Google fonts
  useEffect(() => {
    if (!document.getElementById('triage-fonts')) {
      const link = document.createElement('link');
      link.id   = 'triage-fonts';
      link.rel  = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700&family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: t.colors.bg }}>
      <LoadingScreen />
    </div>
  );

  // Gate: onboarding not complete
  if (config && !config.onboarding_complete) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: t.colors.bg }}>
        <div style={{
          padding:       '16px',
          borderBottom:  `1px solid ${t.colors.border}`,
          background:    t.colors.bgElevated,
          flexShrink:    0,
        }}>
          <div style={{ fontSize: '18px', fontWeight: 600, fontFamily: t.fonts.display,
            color: t.colors.textPrimary, letterSpacing: '0.03em' }}>
            Set up Triage
          </div>
          <div style={{ fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.mono, marginTop: '2px' }}>
            5 minutes — runs automatically after
          </div>
        </div>
        <OnboardingFlow userId={userId} onComplete={handleOnboardingComplete} />
      </div>
    );
  }

  return (
    <div style={{
      display:       'flex',
      flexDirection: 'column',
      height:        '100dvh',
      background:    t.colors.bg,
      overflow:      'hidden',
    }}>
      <TopBar
        screen={screen}
        hasNotif={hasNotif}
        onNotifPress={handleNotifPress}
        config={config}
      />

      {/* Screen content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {screen === 'history'  && <TriageHistory userId={userId} notification={notification} />}
        {screen === 'inbox'    && <TriageInbox   userId={userId} />}
        {screen === 'settings' && <TriageSettings userId={userId} />}
        {screen === 'admin'    && isAdmin && <AdminTriage />}
      </div>

      <BottomNav
        screen={screen}
        onChange={setScreen}
        isAdmin={isAdmin}
        hasNotif={hasNotif}
      />
    </div>
  );
}
